import {getTestProps} from '@benchling/js-test-utils';
import reactUpdate from 'immutability-helper';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as browser from 'lib/Browser';
import * as MediocreConstants from 'notebook/mediocre/MediocreConstants';
import {MEDIOCRE_OVERLAY_SELECTOR} from 'notebook/mediocre/search/DOM';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {LockedCellCheckArgs} from 'notebook/tables/core-ui/TableEditable';
import {TypedValue} from 'notebook/tables/TableConstants';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import * as keyboard from 'util/Keyboard';
import pick from 'util/pick';
import {
  ElementClipboardEvent,
  ElementKeyboardEvent,
  ElementMouseEvent,
  InputChangeEvent,
  Optional,
} from 'util/Types';

type MultiExtra = object;

export interface MultiSelectState<Index> {
  multiStartIndex: Index | null;
  multiEndIndex: Index | null;
  multiActive: string | boolean | null;
  multiSelIndex: Index | null;
  multiActualIndex: Index | null;
  multiExtra: MultiExtra;
}

export interface MetadataForRestore<Index> {
  multiSelectKey: string;
  data: {
    multiStartIndex: Index;
    multiEndIndex: Index;
    multiSelIndex: Index;
    multiExtra?: MultiExtra;
    multiActive?: string | boolean | null;
    multiActualIndex?: Index;
  };
}

export interface MultiSelectable<Index> {
  focusBearerRef: React.RefObject<React.ReactInstance>;
}

export function multiSelectableWillMount<Index>(
  metadataForRestore: MetadataForRestore<Index> | undefined | null,
  multiSelectKey: string,
  setState: (state: Partial<MultiSelectState<Index>>) => void
): void {
  _loadMetadataForRestore(metadataForRestore, multiSelectKey, setState);
}

export function multiSelectableDidMount<Index>(
  mouseDownHandler: (e: MouseEvent) => void,
  mouseUpHandler: (e: MouseEvent) => void
): void {
  if (browser.isBrowser) {
    // Let a component cleanup before anyone handles the event (capture=true).
    window.addEventListener('mousedown', mouseDownHandler, true);
    window.addEventListener('mouseup', mouseUpHandler, true);
  }
}

export function multiSelectableWillUnmount<Index>(
  mouseDownHandler: (e: MouseEvent) => void,
  mouseUpHandler: (e: MouseEvent) => void
): void {
  if (browser.isBrowser) {
    window.removeEventListener('mousedown', mouseDownHandler, true);
    window.removeEventListener('mouseup', mouseUpHandler, true);
  }
}

export function multiSelectableWillReceiveProps<Index>(
  currentProps: {locked: boolean; metadataForRestore?: MetadataForRestore<Index> | null},
  nextProps: {locked: boolean; metadataForRestore?: MetadataForRestore<Index> | null},
  multiSelectKey: string,
  setState: (state: Partial<MultiSelectState<Index>>) => void
): void {
  if (nextProps.locked && !currentProps.locked) {
    clearMultiSelect(setState);
  }
  // If the parent has pushed a new selection to restore,
  // restore the state.
  if (nextProps.metadataForRestore !== currentProps.metadataForRestore) {
    _loadMetadataForRestore(nextProps.metadataForRestore, multiSelectKey, setState);
  }
}

export function multiSelectableDidUpdate<Index>(options: {
  domNode: Element;
  multiEditing: boolean;
  improperFocusHook: (() => void) | undefined;
  multiStartIndex: Index;
  multiEndIndex: Index;
  focusBearerNode: Element;
}): void {
  // Hack: Avoid case where the browser steals focus from this component when we expect it to have focus.
  // We expect it to have focus if we have a selection (whose focus is managed by the focusBearer), but only
  // when:
  //  a. when we aren't actively editing a cell (focus would be managed by the FormaulEditable), OR
  //  b. we've opened a modal that needs focus (e.g SetSelectorModal)... as long that modal is not the one
  //     that this component is rendered inside of (QuickEditModal)
  //  c. we've opened a dropdown that may be interactive
  const noDropdownContainer = $('.absoluteDropdownContainer').length === 0;
  const noActiveOverlay =
    !document.activeElement || $(document.activeElement).closest(MEDIOCRE_OVERLAY_SELECTOR).length === 0;
  if (
    hasMultiSelection(options.multiStartIndex, options.multiEndIndex) &&
    !options.multiEditing &&
    !isFocusSupercededByModal(options.domNode) &&
    noDropdownContainer &&
    noActiveOverlay
  ) {
    _focus(options.focusBearerNode);
  }

  // ReactDOM.findDOMNode(@refs.focusBearer) did not always work here, even when the element
  // was actually in the DOM. This happens when you click on the "expand table" arrow. Instead,
  // as a hacky fix, just find the element in the DOM directly by classname.
  const focusBearerEl = $(options.domNode).find('.js-focusBearer')[0];

  const selectedOrActiveElements = [document.activeElement];
  if (browser.isBrowser) {
    const range = selutil.getRange();
    if (range != null) {
      selectedOrActiveElements.push(range.startContainer);
    }
  }

  // We want to blur if there is no multi-selection, and any of focus bearer's children are active or
  // have an active selection.
  if (
    !hasMultiSelection(options.multiStartIndex, options.multiEndIndex) &&
    selectedOrActiveElements.some((el) => focusBearerEl && focusBearerEl.contains(el))
  ) {
    _blur(options.focusBearerNode);
    if (typeof options.improperFocusHook === 'function') {
      options.improperFocusHook();
    }
  }
}

// # Component-callable helper methods

export function sortedMultiRange<Index>(
  multiStartIndex: Index,
  multiEndIndex: Index,
  compareIndex: (start: Index, end: Index) => number
): {start: Index; end: Index} | null {
  let start = multiStartIndex;
  let end = multiEndIndex;
  if (start != null && end != null) {
    if (compareIndex(start, end) > 0) {
      [start, end] = Array.from([end, start]);
    }
    return {start, end};
  } else {
    return null;
  }
}

// Takes the index of the note part with the current selection (could be null if multi-select).
// If there is a multi-select, returns the multi-range.
// Otherwise, returns a range containing just the passed in index.
export function getSelectedRange<Index>(
  index: Index,
  options: {
    multiStartIndex: Index;
    multiEndIndex: Index;
    compareIndex: (start: Index, end: Index) => number;
  }
): {start: Index; end: Index} | null {
  const multiRange = sortedMultiRange(options.multiStartIndex, options.multiEndIndex, options.compareIndex);
  if (multiRange != null) {
    return multiRange;
  } else if (index != null) {
    return {start: index, end: index};
  } else {
    return null;
  }
}

export function saveMultiSelect<Index>(
  state: Partial<MultiSelectState<Index>> | {selection: SelectionRestoreData},
  storeState: ((state: MetadataForRestore<Index>) => void) | undefined,
  multiSelectKey: string
): void {
  if (typeof storeState === 'function') {
    storeState(computeState(state, multiSelectKey));
  }
}

export function clearMultiSelect<Index>(setState: (state: Partial<MultiSelectState<Index>>) => void): void {
  setState({
    multiStartIndex: null,
    multiEndIndex: null,
    multiActive: null,
    multiSelIndex: null,
    multiActualIndex: null,
    multiExtra: {},
  });
}

export function setMultiExtra<Index>(
  multiExtraUpdate: Partial<MultiExtra>,
  setState: (state: Partial<MultiSelectState<Index>>) => void,
  multiExtra: MultiExtra
): void {
  setState({multiExtra: reactUpdate(multiExtra, {$merge: multiExtraUpdate})});
}

function _updateMultiEditing<Index>(
  {
    index,
    text,
    typedValue,
    editing,
    selection,
  }: {
    index: Index | null;
    text?: string | null;
    typedValue?: TypedValue | null;
    editing: boolean;
    selection?: BenchlingSelection | null;
  },
  options: {
    setMultiEditing:
      | ((args: {
          index: Index | null;
          editing: boolean;
          text: string;
          typedValue: Optional<TypedValue>;
          selection: BenchlingSelection | null;
        }) => void)
      | undefined;
    getMultiEditingText: (index: Index) => string;
    getMultiEditingTypedValue: (index: Index) => TypedValue | null;
  }
): void {
  // Update the multiEditing state if this component (e.g. Tables) supports it.
  if (options.setMultiEditing != null) {
    // If index is passed, it will look up the text using the component's getMultiEditingText.
    // Otherwise, it will use the specified text.
    if (index != null) {
      if (text == null) {
        text = options.getMultiEditingText(index);
      }
      if (typedValue == null || isTypedValueEmpty(typedValue)) {
        typedValue = options.getMultiEditingTypedValue(index);
      }
    }
    if (selection == null) {
      selection = null;
    }
    options.setMultiEditing({editing, index, text: text!, typedValue, selection});
  }
}

export interface MultiSelectSingleParam<Index> {
  text?: string | null;
  typedValue?: TypedValue | null;
  editing?: boolean;
  collapse?: boolean;
  multiActualIndex?: Index;
  selection?: BenchlingSelection;
}

export function multiSelectSingle<Index>(
  index: Index,
  param: MultiSelectSingleParam<Index> | undefined,
  options: {
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    multiStartIndex: Index;
    multiEndIndex: Index;
    setMultiEditing:
      | ((args: {
          index: Index | null;
          editing: boolean;
          text: string;
          typedValue: Optional<TypedValue>;
          selection: BenchlingSelection | null;
        }) => void)
      | undefined;
    getMultiEditingText: (index: Index) => string;
    getMultiEditingTypedValue: (index: Index) => TypedValue | null;
    focusBearerNode: Element;
  }
): void {
  if (param == null) {
    param = {};
  }
  const {text, typedValue, multiActualIndex, selection} = param;
  let editing = param.editing;
  let collapse = param.collapse;
  if (editing == null) {
    editing = false;
  }
  if (collapse == null) {
    collapse = true;
  }
  options.setState({
    multiSelIndex: index,
    multiActualIndex: multiActualIndex != null ? multiActualIndex : index,
    multiStartIndex: collapse ? index : options.multiStartIndex,
    multiEndIndex: collapse ? index : options.multiEndIndex,
  });

  // Use text if specified (which should be passed whenever the cell at index
  // has been set but not yet stored in the table), instead of
  // looking it up in the current table.
  const multiEditingArgs: {
    index: Index | null;
    editing: boolean;
    selection?: BenchlingSelection;
    text?: string;
    typedValue?: TypedValue;
  } = {editing, index, selection};
  if (text != null) {
    multiEditingArgs.text = text;
  }
  if (typedValue != null) {
    multiEditingArgs.typedValue = typedValue;
  }
  _updateMultiEditing(
    text != null ? {text, index, typedValue, editing, selection} : {index, editing, selection},
    options
  );
  _focus(options.focusBearerNode);
}

export function multiCancelEdit<Index>(
  index: Index,
  options: {
    setMultiEditing:
      | ((args: {
          index: Index | null;
          editing: boolean;
          text: string;
          typedValue: Optional<TypedValue>;
          selection: BenchlingSelection | null;
        }) => void)
      | undefined;
    getMultiEditingText: (index: Index) => string;
    getMultiEditingTypedValue: (index: Index) => TypedValue | null;
    focusBearerNode: Element;
  }
): void {
  _updateMultiEditing({index, editing: false}, options);
  _focus(options.focusBearerNode);
}

export function setMultiIndices<Index>(
  {start, end, text}: {start: Index; end: Index; text?: string},
  options: {
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    setMultiEditing:
      | ((args: {
          index: Index | null;
          editing: boolean;
          text: string;
          typedValue: Optional<TypedValue>;
          selection: BenchlingSelection | null;
        }) => void)
      | undefined;
    getMultiEditingText: (index: Index) => string;
    getMultiEditingTypedValue: (index: Index) => TypedValue | null;
    focusBearerNode: Element;
  }
): void {
  options.setState({
    multiStartIndex: start,
    multiSelIndex: start,
    multiActualIndex: start,
    multiEndIndex: end,
  });

  const editOpts = text != null ? {editing: false, index: start, text} : {editing: false, index: start};
  _updateMultiEditing(editOpts, options);

  _focus(options.focusBearerNode);
}

export function isSelected<Index>(
  index: Index,
  multiStartIndex: Index,
  multiEndIndex: Index,
  isIndexBetween: (startIndex: Index, endIndex: Index, index: Index) => boolean
): boolean {
  const startIndex = multiStartIndex;
  const endIndex = multiEndIndex;
  if (!(startIndex != null && endIndex != null)) {
    return false;
  }
  return isIndexBetween(startIndex, endIndex, index);
}

export function hasMultiSelection<Index>(multiStartIndex: Index, multiEndIndex: Index): boolean {
  return multiStartIndex != null && multiEndIndex != null;
}

function _multiCrossEditingHandler<Index>(
  {startIndex, endIndex, active}: {startIndex: Index; endIndex: Index; active: boolean | string},
  e: ElementMouseEvent,
  options: {
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    multiTryInsert: (args: {
      index: Index;
      cid: string;
      start: number;
      end: number;
      shift: boolean;
    }) => boolean;
    multiCrossEditing: boolean;
  }
): boolean {
  if (!options.multiCrossEditing) {
    return false;
  }
  const selectionIndices = selutil.getSelectionIndices();
  if (selectionIndices == null) {
    return false;
  }
  // If we are editing a table...
  const {cid, start, end} = selectionIndices;
  const didInsert = options.multiTryInsert({index: startIndex, cid, start, end, shift: e.shiftKey});
  if (!didInsert) {
    return false;
  }
  // ...and we were able to insert the current multiselect
  // 1. Start multiselecting in this table.
  options.setState({multiActive: active});
  // 2. Prevent the table that is being edited from giving up focus...
  e.stopPropagation(); // prevent window handler from finalizing the edit
  e.preventDefault(); // prevent the focus-bearer from losing focus
  return true;
}

// # Component-registered handlers to manage multiselect state.

export function multiEditableMouseDownHandler<Index>(
  args: {
    start: Index;
    end?: Index;
    active?: boolean | string;
    allowSingleOverride?: boolean;
    isMultiExpandBuffered?: boolean;
  },
  e: ElementMouseEvent,
  options: {
    multiStartIndex: Index;
    multiEndIndex: Index;
    multiSelIndex: Index;
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    allowSingleMultiSelect: boolean;
    multiTryInsert: (args: {
      index: Index;
      cid: string;
      start: number;
      end: number;
      shift: boolean;
    }) => boolean;
    multiCrossEditing: boolean;
    multiFinalizeEditHandler: (() => void) | undefined;
    isIndexBetween: (start: Index, end: Index, index: Index) => boolean;
    compareIndex: (start: Index, end: Index) => number;
    setMultiEditing:
      | ((args: {
          index: Index | null;
          editing: boolean;
          text: string;
          typedValue: Optional<TypedValue>;
          selection: BenchlingSelection | null;
        }) => void)
      | undefined;
    getMultiEditingText: (index: Index) => string;
    getMultiEditingTypedValue: (index: Index) => TypedValue | null;
    focusBearerNode: Element;
    disallowShiftExpand: boolean;
  }
): void {
  // A child component has already handled the event or is an input element, which should be clickable,
  // so let's not do anything.
  // This allows for a user to be making a selection inside of a TableEditable without accidentally losing
  // their selection if they drag out of the table. Similarly, it avoids odd state issues when a user is
  // cross-editing a table from another table and drags out.
  if (e.isDefaultPrevented() || $(e.target).is('input, textarea')) {
    clearMultiSelect(options.setState);
    return;
  }

  const {start: startIndex, end: endIndex, allowSingleOverride} = args;
  const active = args.active;
  assert(active != null, 'the onDown active value must be truthy');
  assert(startIndex != null, 'the onDown start index must exist');
  const allowSingle = allowSingleOverride || options.allowSingleMultiSelect;

  // Sets/expands multiselect on click if we are updating a different multiselect.
  if (_multiCrossEditingHandler({startIndex, endIndex, active}, e, options)) {
    return;
  }

  // Otherwise, finalize the current selection,
  if (typeof options.multiFinalizeEditHandler === 'function') {
    options.multiFinalizeEditHandler();
  }

  // and then extend/set the multiselect

  const selectionInRange =
    options.multiEndIndex != null &&
    options.isIndexBetween(options.multiStartIndex, options.multiEndIndex, args.start);

  // if the user right-clicks within the selection, no-op to trigger contextmenu
  if (e.button === 2 && selectionInRange) {
    return;
  }

  // stops active selection on right-click
  const multiActive = e.button === 2 ? null : active;

  if (e.shiftKey && options.multiStartIndex != null) {
    if (options.disallowShiftExpand) {
      return;
    }
    // If the user provides a startIndex and an endIndex, we set the selection to that,
    // maintaining the multiSelIndex if possible. Otherwise, we interpret it as the user
    // trying to extend the selection from multiSelIndex to the specified startIndex.
    let newEnd;
    let newSel;
    let newStart;
    if (endIndex != null) {
      newStart = startIndex;
      newEnd = endIndex;
      newSel = options.isIndexBetween(newStart, newEnd, options.multiSelIndex)
        ? options.multiSelIndex
        : startIndex;
    } else {
      newStart = options.multiSelIndex;
      newEnd = startIndex;
      newSel = options.multiSelIndex;
    }

    // Prevent a parent multiselect from handling multiselect contained within a single child.
    if (options.compareIndex(newStart, newEnd) === 0 && !allowSingle) {
      return;
    }

    options.setState({
      multiStartIndex: newStart,
      multiSelIndex: newSel,
      multiActualIndex: newSel,
      multiEndIndex: newEnd,
      multiActive,
    });
    _updateMultiEditing({editing: false, index: options.multiSelIndex}, options);
    // Avoid losing focus
    e.preventDefault();
    _focus(options.focusBearerNode);
  } else {
    options.setState({
      multiStartIndex: startIndex,
      multiSelIndex: startIndex,
      multiActualIndex: startIndex,
      multiEndIndex: allowSingle ? (endIndex != null ? endIndex : startIndex) : null,
      multiActive,
      multiExtra: {isMultiExpandBuffered: args.isMultiExpandBuffered || false},
    });
    _updateMultiEditing({editing: false, index: startIndex}, options);
    if (allowSingle) {
      // Avoid losing focus if we are showing single multi-select (materials/steps dont)
      e.preventDefault(); // prevent the focus-bearer from losing focus.
      _focus(options.focusBearerNode); // ensure that it has focus
    }
  }
}

/**
 * A mousemove event handler that handles expanding selection to make horizontal selections across narrow
 * text items less finicky. Empirically, we've seen that users will inadvertently move their mouse vertically
 * while making a horizontal selection, where they end up accidentally triggering a multi-item selection.
 *
 * This behavior is opted into via multiEditableMouseDownHandler with isMultiExpandBuffered set to true.
 * If it is enabled, then the mouseover handler will no-op on single-line selections and defer handling to
 * this method.
 */
export function multiEditableMouseMoveHandler<Index>(
  index: Index,
  e: MouseEvent,
  options: {
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    multiStartIndex: Index;
    multiEndIndex: Index;
    multiActive: string | boolean | null;
    multiExtra: {isMultiExpandBuffered?: boolean} | null;
    compareIndex: (start: Index, end: Index) => number;
  }
): void {
  if (!options.multiActive) {
    return;
  }
  // Only applies to single-line selection
  if (options.multiEndIndex != null) {
    return;
  }
  // If we are not buffered, then selection expansion was already handled by multiEditableMouseOverHandler
  const isMultiExpandBuffered = options.multiExtra && options.multiExtra.isMultiExpandBuffered;
  if (!isMultiExpandBuffered) {
    return;
  }
  // If the user hasn't moved their mouse to a new item, maintain the single selection.
  const cmp = options.compareIndex(options.multiStartIndex, index);
  if (cmp === 0) {
    return;
  }
  // If the user has moved their mouse to a new item, require that they move the mouse
  // MEDIOCRE_ITEM_SELECTION_BUFFER_PX pixels into the item before switching to multi-selection mode.
  const rect = (e.target as HTMLElement).getBoundingClientRect();
  const inset = cmp < 0 ? e.clientY - rect.top : rect.bottom - e.clientY;
  if (inset < MediocreConstants.MEDIOCRE_ITEM_SELECTION_BUFFER_PX) {
    return;
  }
  options.setState({multiEndIndex: index});
}

export function multiEditableMouseOverHandler<Index>(
  index: Index,
  options: {
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    multiActive: string | boolean | null;
    multiStartIndex: Index;
    multiEndIndex: Index;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    multiExtra: {isMultiExpandBuffered?: boolean; [k: string]: any} | null;
    disallowMultiExpand: boolean;
    multiCrossEditing: boolean;
    allowSingleMultiSelect: boolean;
    multiTryInsert: (args: {index: Index; cid: string; start: number; end: number; shift: boolean}) => void;
    compareIndex: (start: Index, end: Index) => number;
    focusBearerNode: Element;
  }
): void {
  if (!options.multiActive) {
    return;
  }
  if (options.disallowMultiExpand) {
    return;
  }
  // If a single-line selection expansion is buffered, it will be handled via multiEditableMouseMoveHandler.
  const isMultiExpandBuffered = options.multiExtra && options.multiExtra.isMultiExpandBuffered;
  if (options.multiEndIndex == null && isMultiExpandBuffered) {
    return;
  }
  // Handle inserting references (e.g. across inserting a table reference)
  if (options.multiCrossEditing) {
    const {cid, start, end} = selutil.getSelectionIndices();
    options.multiTryInsert({index, cid, start, end, shift: true});
    return;
  }
  // If the selection was already single (e.g. via allowSingleOverride), preserve it here.
  const allowSingle = options.allowSingleMultiSelect || options.multiStartIndex === options.multiEndIndex;
  if (options.compareIndex(index, options.multiStartIndex) === 0 && !allowSingle) {
    options.setState({multiEndIndex: null});
    _blur(options.focusBearerNode);
  } else {
    options.setState({multiEndIndex: index});
  }
}

// # Focus-bearer event handlers

function _resetFocusText<Index>(multiSelectable: MultiSelectable<Index>): void {
  $(ReactDOM.findDOMNode(multiSelectable.focusBearerRef.current)!).html('&nbsp;');
  selutil.selectAll(ReactDOM.findDOMNode(multiSelectable.focusBearerRef.current));
}

function _keyDownHandler<Index>(
  e: ElementKeyboardEvent,
  options: {
    state: MultiSelectState<Index>;
    isLockedWithEditWarn: (args?: LockedCellCheckArgs) => boolean;
    restoreState:
      | ((
          e: ElementKeyboardEvent,
          selectionRestoreData: MetadataForRestore<Index> | {selection: SelectionRestoreData}
        ) => boolean)
      | undefined;
    setState: (state: Partial<MultiSelectState<Index>>) => void;
    setMultiEditing:
      | ((args: {
          index: Index | null;
          editing: boolean;
          text: string;
          typedValue: Optional<TypedValue>;
          selection: BenchlingSelection | null;
        }) => void)
      | undefined;
    getMultiEditingText: (index: Index) => string;
    getMultiEditingTypedValue: (index: Index) => TypedValue | null;
    firstIndex: Index;
    lastIndex: Index;
    multiKeyDownHandler: (e: ElementKeyboardEvent) => void;
    multiSelectable: MultiSelectable<Index>;
    multiSelectKey: string;
  }
): void {
  // Let the history manager handle Ctrl-Z
  if (keyboard.isUndoRedo(e)) {
    if (options.isLockedWithEditWarn({forUndoRedo: true})) {
      e.preventDefault();
    } else if (typeof options.restoreState === 'function') {
      options.restoreState(e, computeState(options.state, options.multiSelectKey));
    }
    return;
  }
  if (keyboard.isCtrlA(e)) {
    e.preventDefault();
    setMultiIndices(
      {
        start: options.firstIndex,
        end: options.lastIndex,
      },
      {
        ...options,
        focusBearerNode: ReactDOM.findDOMNode(options.multiSelectable.focusBearerRef.current) as Element,
      }
    );
  } else {
    options.multiKeyDownHandler(e);
  }

  // Reset text to non-empty string and select all to ensure:
  //  1. one selected character for copy in the clipboard (for IE)
  //  2. all text is selected to replace all text onInput
  _resetFocusText(options.multiSelectable);
}

function _inputHandler<Index>(
  e: InputChangeEvent,
  options: {
    multiSelIndex: Index;
    multiInputHandler: (args: {index?: Index; text: string}) => void;
    isLockedWithEditWarn: (args?: LockedCellCheckArgs) => boolean;
    multiSelectable: MultiSelectable<Index>;
  }
): void {
  // on input (does not include backspace, paste, etc.), we only check if the anchor cell is locked
  // to prevent editing.
  if (!options.isLockedWithEditWarn({onlyAnchor: true})) {
    // Grab the text from the focus bearer...
    const text = $(ReactDOM.findDOMNode(options.multiSelectable.focusBearerRef.current)!).text();
    options.multiInputHandler({index: options.multiSelIndex, text});
  }

  // ...and clear out the input since we don't need the text anymore.
  _resetFocusText(options.multiSelectable);
}

function _setClipboard<TCopyOptions>(
  e: ElementClipboardEvent,
  multiGetHTMLForCopy: (copyOptions: TCopyOptions) => string,
  multiGetTextForCopy: ((copyOptions: TCopyOptions) => string) | undefined,
  multiCopyOptions: TCopyOptions
): void {
  e.clipboardData.setData('text/html', multiGetHTMLForCopy(multiCopyOptions));
  if (multiGetTextForCopy != null) {
    e.clipboardData.setData('text/plain', multiGetTextForCopy(multiCopyOptions));
  }
}

function _copyHandler<TCopyOptions>(
  e: ElementClipboardEvent,
  options: {
    multiGetHTMLForCopy: (copyOptions: TCopyOptions) => string;
    multiGetTextForCopy: ((copyOptions: TCopyOptions) => string) | undefined;
    multiCopyOptions: TCopyOptions;
  }
): void {
  _setClipboard(e, options.multiGetHTMLForCopy, options.multiGetTextForCopy, options.multiCopyOptions);
  e.preventDefault();
}

function _cutHandler<Index, TCopyOptions>(
  e: ElementClipboardEvent,
  options: {
    multiSelIndex: Index;
    multiGetHTMLForCopy: (copyOptions: TCopyOptions) => string;
    multiGetTextForCopy: ((copyOptions: TCopyOptions) => string) | undefined;
    multiInputHandler: (args: {index: Index; text: string}) => void;
    isLockedWithEditWarn: () => boolean;
    multiCopyOptions: TCopyOptions;
  }
): void {
  if (!options.isLockedWithEditWarn()) {
    _setClipboard(e, options.multiGetHTMLForCopy, options.multiGetTextForCopy, options.multiCopyOptions);
    options.multiInputHandler({index: options.multiSelIndex, text: ''});
  }
  e.preventDefault();
}

function _pasteHandler(
  e: ElementClipboardEvent,
  options: {
    isLockedWithEditWarn: () => boolean;
    multiPasteHandler: (e: ElementClipboardEvent) => void;
  }
): void {
  if (options.isLockedWithEditWarn()) {
    e.preventDefault();
    return;
  }
  options.multiPasteHandler(e);
}

// # Window event handlers

function _wouldMultiInsert<Index>(
  e: MouseEvent,
  multiEditing: boolean,
  multiCanInsert: ((args: {cid: string; start: number; end: number; shift: boolean}) => boolean) | undefined,
  multiNamespace: string | undefined
): boolean {
  if (!multiEditing || multiCanInsert == null || multiNamespace == null) {
    return false;
  }

  // Clicked item must be allowed to interact with this item (same namespace)
  if (!$(e.target!).closest(`[data-namespace=${multiNamespace}]`).length) {
    return false;
  }

  // We expect the clicked item to handle the event:
  const selectionIndices = selutil.getSelectionIndices();
  if (selectionIndices == null) {
    return false;
  }

  const {cid, start, end} = selectionIndices;
  return multiCanInsert({cid, start, end, shift: e.shiftKey});
}

export function multiWindowMouseDownHandler<Index>(
  e: MouseEvent,
  options: {
    multiEditing: boolean;
    multiCanInsert:
      | ((args: {cid: string; start: number; end: number; shift: boolean}) => boolean)
      | undefined;
    multiNamespace: string | undefined;
    multiFinalizeEditHandler: (() => void) | undefined;
    domNode: Element;
    multiStartIndex: Index;
    multiEndIndex: Index;
    setMultiEditing:
      | ((args: {index: Index | null; editing: boolean; text: string | null}) => void)
      | undefined;
    setState: (state: Partial<MultiSelectState<Index>>) => void;
  }
): void {
  // If the clicked on item will be inserting into this component, let it handle the event.
  if (_wouldMultiInsert(e, options.multiEditing, options.multiCanInsert, options.multiNamespace)) {
    return;
  }

  // If we are clicking inside of a cross step group, don't clear the state.
  if (
    BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS &&
    $(e.target!).closest('.multi-window-editing-container').length > 0
  ) {
    return;
  }

  if (
    $(e.target!).hasClass('mediocre-tableEditable-scrollArea') ||
    $(e.target!).closest('.table-collapse-control').length > 0
  ) {
    // If we are clicking the scrollbar or collapsing/expanding a table, don't clear the state
    // and prevent the focus-bearer from losing focus.
    e.preventDefault();
    return;
  }

  if ($(e.target!).closest('.dropdown-menu, .backdrop, .menu').length) {
    // If we are clicking into a menu, don't clear the state either
    return;
  }

  // If we were multi-editing, let the component know to finalize any state.
  if (options.multiEditing) {
    // If the component we are clicking is a rich UI component for the currently active table cell,
    // even if it's being rendered via a Portal / AbsoluteDropdownContainer, do not auto dismiss.
    //
    // Components should either include the js-mediocre-active-edit class on them, or be listed here to have
    // their click events ignored.
    if ($(e.target!).closest('.js-mediocre-active-edit, .attachDropdown').length > 0) {
      return;
    }
    if (typeof options.multiFinalizeEditHandler === 'function') {
      options.multiFinalizeEditHandler();
    }
  }

  // The subcomponent that was clicked can handle updating state, or not (e.g. add row/col btns)
  if ($(e.target!).closest(options.domNode).length) {
    return;
  }

  // If we are clicking while a modal that is not the container of this element is open,
  // don't clear the state; this allows the selection to be cleared when the component is
  // rendered inside a modal, e.g. QuickEditModal
  const modal = $('.modal:visible').last()[0];
  if (modal != null && !modal.contains(options.domNode)) {
    return;
  }

  // If we had a selection, let's clear it.
  if (hasMultiSelection(options.multiStartIndex, options.multiEndIndex)) {
    if (typeof options.setMultiEditing === 'function') {
      options.setMultiEditing({editing: false, index: null, text: null});
    }
    clearMultiSelect(options.setState);
  }
}

export function multiWindowMouseUpHandler<Index>(
  e: MouseEvent,
  options: {
    multiActive: string | boolean | null;
    multiCrossEditing: boolean;
    setState: (state: Partial<MultiSelectState<Index>>) => void;
  }
): void {
  // Stops active selection on mouseup
  if (options.multiActive || options.multiCrossEditing) {
    options.setState({multiActive: null});
  }
}

// # Internal helpers

export function computeState<Index>(
  state: Partial<MultiSelectState<Index>> | {selection: SelectionRestoreData},
  multiSelectKey: string
): {
  multiSelectKey: string;
  data: {multiActive: null; multiStartIndex: Index; multiEndIndex: Index; multiSelIndex: Index};
} {
  return {
    multiSelectKey,
    // eslint-disable-next-line no-restricted-syntax
    data: _.extend(
      {multiActive: null},
      pick(state as Partial<MultiSelectState<Index>>, 'multiStartIndex', 'multiEndIndex', 'multiSelIndex')
    ),
  };
}

export function _focus(focusBearerNode: Element): void {
  // Only focus if we don't already have focus (since focusing is somewhat slow.. a few ms)
  if (document.activeElement !== focusBearerNode) {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    }
    $(focusBearerNode).focus();
  }
}

export function _blur(focusBearerNode: Element): void {
  if (document.activeElement === focusBearerNode) {
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
      }
    }
    $(focusBearerNode).blur();
  }
}

function _loadMetadataForRestore<Index>(
  metadataForRestore: {multiSelectKey: string; data: Partial<MultiSelectState<Index>>} | null | undefined,
  multiSelectKey: string,
  setState: (state: Partial<MultiSelectState<Index>>) => void
): void {
  const metadata = metadataForRestore;
  // If we received metadata, and it has our selection key, and we have a selection, restore it.
  // TODO(BNCH-40430): SpreadsheetStore is not properly restored for tables.
  if (metadata != null && metadata.multiSelectKey === multiSelectKey) {
    setState(metadata.data);
  } else {
    clearMultiSelect(setState);
  }
}

export interface RenderFocusBearerArgs<Index, TCopyOptions = void> {
  ref: React.ForwardedRef<HTMLDivElement>;
  state: MultiSelectState<Index>;
  isLockedWithEditWarn: () => boolean;
  restoreState:
    | ((
        e: ElementKeyboardEvent,
        selectionRestoreData: MetadataForRestore<Index> | {selection: SelectionRestoreData}
      ) => boolean)
    | undefined;
  setState: (state: Partial<MultiSelectState<Index>>) => void;
  setMultiEditing:
    | ((args: {
        index: Index | null;
        editing: boolean;
        text: string;
        typedValue: Optional<TypedValue>;
        selection: BenchlingSelection | null;
      }) => void)
    | undefined;
  getMultiEditingText: (index: Index) => string;
  getMultiEditingTypedValue: (index: Index) => TypedValue | null;
  firstIndex: Index;
  lastIndex: Index;
  multiSelectable: MultiSelectable<Index>;
  multiSelectKey: string;
  multiSelIndex: Index | null;
  multiKeyDownHandler: (e: ElementKeyboardEvent) => void;
  multiInputHandler: (args: {index: Index; text: string}) => void;
  multiPasteHandler: (e: ElementClipboardEvent) => void;
  multiGetHTMLForCopy: (options: TCopyOptions) => string;
  multiGetTextForCopy: ((options: TCopyOptions) => string) | undefined;
  multiCopyOptions: TCopyOptions;
  dataTestAttrs: DataTestAttrs;
}

export function renderFocusBearer<Index, TCopyOptions>(
  args: RenderFocusBearerArgs<Index, TCopyOptions>
): JSX.Element {
  const keyDownHandler = (e: ElementKeyboardEvent): void => {
    _keyDownHandler(e, args);
  };
  const inputHandler = (e: InputChangeEvent): void => {
    _inputHandler(e, args);
  };
  const pasteHandler = (e: ElementClipboardEvent): void => {
    _pasteHandler(e, args);
  };
  const copyHandler = (e: ElementClipboardEvent): void => {
    _copyHandler<TCopyOptions>(e, args);
  };
  const cutHandler = (e: ElementClipboardEvent): void => {
    assert(args.multiSelIndex != null, 'Unable to cut without selected index');
    const {multiSelIndex} = args;
    _cutHandler<Index, TCopyOptions>(e, {...args, multiSelIndex});
  };
  return (
    <div
      ref={args.ref}
      key="focus-bearer"
      className="hiddenFocusEditable js-focusBearer"
      {...getTestProps({component: 'FocusBearer', element: 'root'})}
      tabIndex={-1}
      contentEditable={true}
      onKeyDown={keyDownHandler}
      onInput={inputHandler}
      onPaste={pasteHandler}
      onCopy={copyHandler}
      onCut={cutHandler}
      {...getTestProps(args.dataTestAttrs)}
    />
  );
}
